import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, CheckCircle, ArrowRight, Shield, AlertTriangle, Award } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import ContactPopup from '../../components/ContactPopup';
import { processSteps } from '../../mock';

const TrademarkInfringementMonitoring = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const benefits = [
    'Continuous USPTO database monitoring',
    'Detect confusingly similar applications',
    'Early detection of potential infringement',
    'Detailed monthly monitoring reports',
    'Opposition filing support',
    'Protect your brand identity',
    'Prevent market confusion',
    'Maintain trademark exclusivity'
  ];

  const packages = [
    {
      name: 'Basic Monitoring',
      price: '$99',
      description: 'Essential monthly monitoring of USPTO database.',
      note: 'Per Month',
      features: [
        'USPTO Database Monitoring',
        'Monthly Monitoring Reports',
        'Identical Mark Detection',
        'Email Alerts',
        'Basic Similarity Analysis'
      ],
      disclaimer: '(Minimum 6-month commitment)'
    },
    {
      name: 'Standard Monitoring',
      price: '$199',
      description: 'Comprehensive monitoring with advanced similarity detection.',
      note: 'Per Month',
      features: [
        'USPTO Database Monitoring',
        'Monthly Monitoring Reports',
        'Identical Mark Detection',
        'Email Alerts',
        'Basic Similarity Analysis',
        'Phonetic Similarity Detection',
        'Visual Similarity Analysis',
        'Common Law Use Monitoring',
        'Priority Support'
      ],
      disclaimer: '(Minimum 6-month commitment)'
    },
    {
      name: 'Premium Monitoring',
      price: '$349',
      description: 'Full-service monitoring with legal analysis and opposition support.',
      note: 'Per Month',
      features: [
        'USPTO Database Monitoring',
        'Monthly Monitoring Reports',
        'Identical Mark Detection',
        'Email Alerts',
        'Basic Similarity Analysis',
        'Phonetic Similarity Detection',
        'Visual Similarity Analysis',
        'Common Law Use Monitoring',
        'Attorney Legal Analysis',
        'Opposition Consultation',
        'Cease & Desist Letter Drafting',
        'Market Use Monitoring',
        'Dedicated Support'
      ],
      popular: true,
      recommended: true,
      disclaimer: '(Minimum 12-month commitment)'
    },
    {
      name: 'Enterprise Monitoring',
      price: '$699',
      description: 'Complete brand protection with portfolio monitoring and enforcement.',
      note: 'Per Month',
      features: [
        'USPTO Database Monitoring',
        'Monthly Monitoring Reports',
        'Identical Mark Detection',
        'Email Alerts',
        'Basic Similarity Analysis',
        'Phonetic Similarity Detection',
        'Visual Similarity Analysis',
        'Common Law Use Monitoring',
        'Attorney Legal Analysis',
        'Opposition Consultation',
        'Cease & Desist Letter Drafting',
        'Market Use Monitoring',
        'Portfolio Management',
        'Multi-Mark Monitoring',
        'Global Trademark Watch',
        'Priority Enforcement Support',
        'Lifetime Support'
      ],
      disclaimer: '(Annual commitment with discounts)'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-900 to-slate-950 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center bg-amber-500/10 border border-white/20 rounded-full px-4 py-2 mb-6">
              <Eye className="h-4 w-4 text-amber-500 mr-2" />
              <span className="text-sm text-amber-500 font-medium">Trademark Services</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Active Trademark <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Infringement Monitoring</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Protect your brand with continuous trademark monitoring. We actively watch for confusingly similar trademark applications and potential infringement to safeguard your intellectual property.
            </p>
            <Button 
              size="lg" 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-xl transform transition-transform hover:scale-105"
            >
              Start Monitoring
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Active Monitoring Is Essential
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Stay ahead of potential infringement and protect your brand.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 bg-slate-900/40 border border-white/20 rounded-lg p-4 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-colors">
                <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Packages */}
      <section className="py-20 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Choose Your Monitoring Plan
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Transparent monthly pricing for ongoing brand protection.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg) => (
              <Card key={pkg.name} className={`bg-slate-900/60 border-white/20 relative ${pkg.popular ? 'border-white/60 shadow-xl shadow-white/10' : 'hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02]'} transition-all duration-300 flex flex-col h-[600px]`}>
                {pkg.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                      RECOMMENDED
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-4 flex-shrink-0">
                  <CardTitle className="text-white text-xl mb-2">{pkg.name}</CardTitle>
                  <div className="text-3xl font-bold text-amber-500 mb-1">{pkg.price}</div>
                  <div className="text-xs text-gray-500 mb-2">{pkg.note}</div>
                  <CardDescription className="text-gray-400 text-sm leading-relaxed">{pkg.description}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col flex-grow">
                  <div className="flex-grow overflow-y-auto mb-4 pr-2" style={{ maxHeight: '250px' }}>
                    <ul className="space-y-2">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-gray-300 text-xs">
                          <CheckCircle className="h-3 w-3 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex-shrink-0">
                    <p className="text-xs text-gray-500 text-center mb-4">{pkg.disclaimer}</p>
                    <Button 
                      onClick={() => setIsPopupOpen(true)}
                      className={`w-full ${pkg.popular ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700' : 'bg-slate-800 hover:bg-slate-700'} text-white font-semibold`}
                    >
                      Get Started
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How Monitoring Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div key={step.step} className="relative">
                <div className="bg-slate-900 border border-white/20 rounded-xl p-8 h-full hover:border-white/40 hover:shadow-xl hover:shadow-white/10 hover:scale-[1.02] transition-all group">
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white w-16 h-16 rounded-full flex items-center justify-center font-black text-2xl mb-6 shadow-xl group-hover:scale-110 transition-transform">
                    {step.step}
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">{step.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <ArrowRight className="hidden md:block absolute -right-4 top-1/2 transform -translate-y-1/2 text-amber-500 h-8 w-8 z-10" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-slate-900/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              What's Included
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-slate-900/60 border-white/20 text-center">
              <CardHeader>
                <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Eye className="h-8 w-8 text-amber-500" />
                </div>
                <CardTitle className="text-white">Continuous Watch</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm">
                  24/7 monitoring of USPTO trademark database for similar marks.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-white/20 text-center">
              <CardHeader>
                <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="h-8 w-8 text-amber-500" />
                </div>
                <CardTitle className="text-white">Instant Alerts</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm">
                  Immediate notifications when potential conflicts are detected.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-white/20 text-center">
              <CardHeader>
                <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-amber-500" />
                </div>
                <CardTitle className="text-white">Legal Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm">
                  Expert review of detected marks with enforcement recommendations.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-20 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              Why Active Monitoring Matters
            </h2>
            <p className="text-xl text-gray-400">
              Don't let infringers dilute your brand value
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-slate-800/30 to-slate-900/30 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
              <div className="text-center mb-6">
                <div className="bg-red-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-red-400 text-3xl">⚠️</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Without Monitoring</h3>
                <p className="text-red-400 font-semibold">High Risk</p>
              </div>
              <ul className="space-y-3">
                {[
                  'Miss infringing trademark filings',
                  'Lose opposition opportunities',
                  'Brand dilution and confusion',
                  'Difficult enforcement later',
                  'Costly litigation may be required',
                  'Loss of trademark rights',
                  'Reduced brand value'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start text-gray-400">
                    <span className="text-red-400 mr-3 mt-1">✗</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 backdrop-blur-sm border-2 border-amber-500/40 rounded-2xl p-8 shadow-xl shadow-amber-500/10">
              <div className="text-center mb-6">
                <div className="bg-amber-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-amber-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">With Our Monitoring</h3>
                <p className="text-amber-400 font-semibold">Fully Protected</p>
              </div>
              <ul className="space-y-3">
                {[
                  'Early detection of conflicts',
                  'Timely opposition filings',
                  'Protect brand exclusivity',
                  'Easy and cost-effective enforcement',
                  'Prevent market confusion',
                  'Maintain trademark strength',
                  'Preserve brand value'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start text-gray-200">
                    <CheckCircle className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        <div className="absolute top-10 right-10 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-amber-500 font-semibold uppercase tracking-wider text-sm">Got Questions?</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
              Frequently Asked
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Questions</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {[
              { q: 'What is trademark monitoring?', a: 'Trademark monitoring is a proactive service that continuously watches for new trademark applications that may conflict with your registered mark, allowing you to take action before they are registered.' },
              { q: 'How often do you check for conflicts?', a: 'We monitor the USPTO database continuously, with automated checks performed daily. You receive comprehensive monthly reports detailing any potential conflicts found.' },
              { q: 'What happens if you find a conflicting mark?', a: 'We immediately alert you and provide a detailed analysis. Depending on your package, we can help you file an opposition, send cease and desist letters, or provide legal consultation on enforcement options.' },
              { q: 'How long should I monitor my trademark?', a: 'We recommend continuous monitoring for as long as you use your trademark. Active monitoring is the best way to protect your brand and prevent infringement.' },
              { q: 'Can you monitor international trademarks?', a: 'Our Enterprise package includes global trademark watch services monitoring major international trademark offices and databases worldwide.' },
              { q: 'What is the opposition window?', a: 'After a trademark is published, there is a 30-day window to file an opposition with the USPTO. Our monitoring ensures you never miss this critical deadline.' }
            ].map((faq, index) => (
              <Accordion key={index} type="single" collapsible>
                <AccordionItem value={`item-${index}`} className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-white/20 rounded-2xl px-6 py-2 hover:border-white/40 hover:shadow-xl hover:shadow-white/10 transition-all group">
                  <AccordionTrigger className="text-white font-bold text-left hover:text-amber-400 py-6 group-hover:scale-[1.01] transition-all">
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/20 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <span className="text-amber-400 font-black text-lg">{index + 1}</span>
                      </div>
                      <span className="flex-1">{faq.q}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 pl-14 pr-4 pb-6 leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              Related Services
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Trademark Registration',
                desc: 'Register your trademark with the USPTO',
                link: '/services/trademark-registration',
                icon: '®'
              },
              {
                title: 'Office Actions Response',
                desc: 'Expert response to USPTO office actions',
                link: '/services/trademark-office-actions',
                icon: '📋'
              },
              {
                title: 'Trademark Renewal',
                desc: 'Keep your trademark registration active',
                link: '/services/trademark-renewal',
                icon: '🔄'
              }
            ].map((service, idx) => (
              <Link key={idx} to={service.link}>
                <Card className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-white/20 hover:border-white/40 hover:shadow-xl hover:shadow-white/10 transition-all h-full group cursor-pointer">
                  <CardHeader>
                    <div className="text-5xl mb-4 group-hover:scale-110 transition-transform">{service.icon}</div>
                    <CardTitle className="text-white text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-gray-400">{service.desc}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center text-amber-400 font-semibold group-hover:translate-x-2 transition-transform">
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 relative overflow-hidden bg-gradient-to-br from-amber-500/20 via-slate-950 to-slate-950">
        <div className="absolute inset-0 backdrop-blur-sm" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-sm border-2 border-white/60 rounded-3xl p-12 shadow-2xl">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6">
              Ready to Protect Your Brand?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Start active monitoring today and safeguard your trademark from infringement.
            </p>
            <Button 
              size="lg" 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold shadow-xl hover:shadow-amber-500/30 hover:scale-105 transition-all px-10"
            >
              Get Started Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default TrademarkInfringementMonitoring;